import { GridRowId } from "@mui/x-data-grid-pro";
import { NavLink } from "react-router-dom";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";
import { DateTime } from "luxon";
import { Box, Chip, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import {
  inactiveColor,
  standardGraphColors,
} from "../../../../theme/siteColors";
import shiColors from "shared-ui/src/theme/shiColors";
import { lighten } from "@mui/material/styles";
import GlobalConstants from "@constants";
import {
  ContractDetailField,
  DetailFieldType,
} from "@features/projects-feature/projectTypes";
import { getPicklistValue } from "../../../../functions/picklistValue";
import { ContractDto } from "@lib/ShiOneClient";
import { FilterOption } from "../../interfaces/ToolbarInterfaces";
import {
  FilterType,
  IFilters,
} from "shared-ui/src/interfaces/ToolbarInterfaces";
import {
  FilterBarProps,
} from "shared-ui/src/components/filter-bar/FilterBar";
import { IncludeExcludeFilterCategory } from "shared-ui/src/components/include-exclude-filter/reducers/includeExcludeFilterReducer";
import {
  hasDateOperatorValues,
} from "shared-ui/src/utils/filterDateOperatorUtils";
import { matchesDateOperatorFilters } from "shared-ui/src/utils/dateOperatorFilterMatching";
import {
  getNonEmptyFilterValues,
  hasActualFilterValue,
} from "shared-ui/src/utils/filterUtils";
import { ProjectData } from "@features/projects-feature/hooks/useDenormalizeProjectsData";

const numberType = "number";

export interface ProjectsTableUserState {
  filters: FilterBarProps;
  viewFilters: Array<FilterOption | IFilters>;
  searchTerm: string;
}

type ProjectFilterConfig = FilterOption;

export const getContractDetailPicklistLabel = (
  field: ContractDetailField,
  contract: ContractDto,
  picklist: any,
): string => {
  const value = contract[field.fieldKey];
  return typeof value === "number" &&
    field.picklistEntityKey &&
    field.picklistFieldKey
    ? getPicklistValue(
        picklist,
        field.picklistEntityKey,
        field.picklistFieldKey,
        value,
      ).label
    : "Unknown";
};

export const formatDateTime = (dateTime: Date): string =>
  DateTime.fromJSDate(dateTime)
    .toUTC()
    .toFormat(GlobalConstants.MuiGridFormats.dateFormat);

export const formatDetailValue = (
  value: any,
  fieldType: DetailFieldType,
): string => {
  switch (fieldType) {
    case DetailFieldType.Date:
      return value instanceof Date ? formatDateTime(value) : "";
    case DetailFieldType.Text:
      return value ? String(value) : "";
    case DetailFieldType.Number:
      return typeof value === numberType ? value.toString() : "";
    default:
      return "Unknown";
  }
};

function isDateFilter(filter: ProjectFilterConfig) {
  return Boolean(filter.datePicker);
}

function isViewDateFilter(filter: FilterOption | IFilters) {
  const legacyFilter = filter as FilterOption;
  const viewFilter = filter as IFilters;

  return (
    Boolean(legacyFilter.datePicker) ||
    viewFilter.filterType === FilterType.DatePicker
  );
}

export function buildFilterBarData(
  initialFilterConfig: ProjectFilterConfig[],
): IncludeExcludeFilterCategory[] {
  return initialFilterConfig
    .map((filter) => {
      const options = filter.options ?? [];

      return {
        id: filter.accessor,
        label: filter.label,
        type: isDateFilter(filter) ? ("date-operator" as const) : ("checkbox" as const),
        options: isDateFilter(filter)
          ? [
              {
                item: "after",
                label: "After",
                checked: false,
                categoryId: filter.accessor,
                dateOperator: "after" as const,
              },
              {
                item: "before",
                label: "Before",
                checked: false,
                categoryId: filter.accessor,
                dateOperator: "before" as const,
              },
            ]
          : options.map((option) => ({
              item: String(option),
              checked: false,
              categoryId: filter.accessor,
            })),
      };
    })
    .filter((filter) => filter.options.length > 0);
}

export function sortProjectFilterCategories(
  categories: IncludeExcludeFilterCategory[],
): IncludeExcludeFilterCategory[] {
  const endLabelsOrder: Record<string, number> = {
    "Start Date": 1,
    "End Date": 2,
  };

  return [...categories].sort((leftFilter, rightFilter) => {
    const leftEndOrder = endLabelsOrder[leftFilter.label] ?? 0;
    const rightEndOrder = endLabelsOrder[rightFilter.label] ?? 0;

    if (leftEndOrder !== rightEndOrder) {
      if (leftEndOrder === 0) {
        return -1;
      }

      if (rightEndOrder === 0) {
        return 1;
      }

      return leftEndOrder - rightEndOrder;
    }

    return leftFilter.label.localeCompare(rightFilter.label);
  });
}

export function buildInitialProjectFilter(
  initialFilterConfig: ProjectFilterConfig[],
): FilterBarProps {
  const include = initialFilterConfig.reduce<Record<string, string[]>>(
    (accumulator, filter) => {
      if (isDateFilter(filter) || !filter.value) {
        return accumulator;
      }

      accumulator[filter.accessor] = [String(filter.value)];

      return accumulator;
    },
    {},
  );

  return {
    include,
    exclude: {},
  };
}

export function hasActiveProjectFilterValues(filter: FilterBarProps) {
  return (
    Object.keys(filter.include).length > 0 ||
    Object.keys(filter.exclude).length > 0
  );
}

export function matchesProjectFilter(
  project: ProjectData,
  filter: FilterBarProps,
) {
  const includeMatch = Object.entries(filter.include).every(
    ([accessor, values]) => {
      if (values.length === 0) {
        return true;
      }

      if (hasDateOperatorValues(values)) {
        return matchesDateOperatorFilters(project[accessor], values);
      }

      return values.includes(String(project[accessor] ?? ""));
    },
  );

  if (!includeMatch) {
    return false;
  }

  return !Object.entries(filter.exclude).some(([accessor, values]) =>
    hasDateOperatorValues(values)
      ? matchesDateOperatorFilters(project[accessor], values)
      : values.includes(String(project[accessor] ?? "")),
  );
}

export function matchesViewFilters(
  project: ProjectData,
  viewFilters: Array<FilterOption | IFilters>,
) {
  return viewFilters.every((filter) => {
    if (isViewDateFilter(filter)) {
      if (filter.startDate && project[filter.accessor] < filter.startDate) {
        return false;
      }

      if (filter.endDate && project[filter.accessor] > filter.endDate) {
        return false;
      }

      return true;
    }

    if (!hasActualFilterValue(filter.value)) {
      return true;
    }

    if (Array.isArray(filter.value)) {
      const nonEmptyValues = getNonEmptyFilterValues(filter.value);
      return nonEmptyValues.includes(project[filter.accessor]);
    }

    return project[filter.accessor] === filter.value;
  });
}

export function getStaleSearchParams(
  searchParams: URLSearchParams,
  filter: FilterBarProps,
) {
  const paramsToDelete: string[] = [];

  if (
    searchParams.has("contractName") &&
    !filter.include.contractName?.length
  ) {
    paramsToDelete.push("contractName");
  }

  return paramsToDelete;
}

export function matchesSearchTerm(project: ProjectData, searchTerm: string) {
  const normalizedSearchTerm = searchTerm.toUpperCase();

  if (!normalizedSearchTerm) {
    return true;
  }

  return (
    project.name.toUpperCase().includes(normalizedSearchTerm) ||
    project.projectLeadName.toUpperCase().includes(normalizedSearchTerm)
  );
}

export function assignProjectName(
  id: GridRowId,
  projectName: string,
  status: string,
  locationSearch: string = "",
  listUrl?: string,
) {
  return (
    <NavLink
      to={AppendIfPrefixExists(`/services/service-engagements/${id}${locationSearch}`)}
      state={{ name: projectName, status: status, listUrl }}
    >
      {projectName}
    </NavLink>
  );
}

export function assignStartDateTime(startDate: Date) {
  return <span>{formatDateTime(startDate)}</span>;
}

export function assignEndDateTime(endDate: Date) {
  return <span>{formatDateTime(endDate)}</span>;
}

export function assignDuration(
  duration: number,
  status: string,
  currentProgress: number,
) {
  let barColor = shiColors.blue.light;
  let progress =
    (currentProgress / duration) * 100 > 100
      ? 100
      : Math.round((currentProgress / duration) * 100);

  let message =
    status === "Complete"
      ? status
      : `${currentProgress} / ${duration} (${progress}%)`;

  if (progress >= 100 && status !== "Complete") {
    message = `Overdue by ${currentProgress - duration} days`;
    barColor = shiColors.red.DEFAULT;
  }
  if (status === "Inactive") {
    message = status;
    progress = 100;
    barColor = shiColors.orange.light;
  }
  return (
    <Box sx={{ width: "100%" }}>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: "10px",
          bgcolor: lighten(shiColors.blue.light, 0.6),
          "& .MuiLinearProgress-bar": { bgcolor: barColor, height: "10px" },
        }}
      />
      <Typography sx={{ float: "right", fontSize: ".8em" }}>
        {message}
      </Typography>
    </Box>
  );
}

export function assignStatus(status: string) {
  return (
    <Chip
      size={"small"}
      sx={{ borderColor: statusChipColors[status] }}
      label={status}
      variant="outlined"
    />
  );
}

export function assignType(type: string) {
  return (
    <Chip
      size={"small"}
      sx={{ borderColor: typeChipColors[type] }}
      label={type}
      variant="outlined"
    />
  );
}

const statusChipColors: { [key: string]: string } = {
  Inactive: inactiveColor,
  New: standardGraphColors[1],
  "In Progress": standardGraphColors[2],
  "Change Order": standardGraphColors[3],
  "On Hold": standardGraphColors[4],
  "Waiting Parts": standardGraphColors[5],
  "At Risk": standardGraphColors[6],
  "Waiting Customer": standardGraphColors[7],
  Complete: standardGraphColors[8],
  Backlog: standardGraphColors[9],
};

const typeChipColors: { [key: string]: string } = {
  Proposal: standardGraphColors[1],
  Template: standardGraphColors[2],
  Internal: standardGraphColors[3],
  Client: standardGraphColors[4],
  Baseline: standardGraphColors[5],
};
